<template>
  <UserLayout>
    <v-container v-if="user" class="account-page py-6 py-md-10">
      <v-card class="profile-hero mb-8" outlined>
        <v-card-text class="pa-6">
          <v-row align="center">
            <v-col cols="12" md="7" class="d-flex align-center">
              <v-avatar color="primary" size="72" class="mr-5 flex-shrink-0">
                <span class="white--text text-h5 font-weight-bold">{{ initials }}</span>
              </v-avatar>
              <div>
                <div class="text-h5 font-weight-bold">
                  {{ displayName }}
                </div>
                <div class="text--secondary mt-1">
                  {{ user.email || displayName }}
                </div>
                <div class="d-flex flex-wrap align-center mt-3">
                  <v-chip small class="text-none mr-2 mb-1" :color="hasPlan ? 'primary' : undefined" :outlined="!hasPlan">
                    {{ planName }}
                  </v-chip>
                  <v-chip
                    v-if="plan && plan.cancel_at_period_end"
                    small
                    color="warning"
                    text-color="white"
                    class="text-none mb-1"
                  >
                    {{ $t('subscriptions.planWillBeCancelled') }}
                  </v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-md-right">
              <div v-if="hasPlan && planPeriodEnd" class="body-2 text--secondary mb-3">
                {{ planRenewTooltip }}
                <DateTimeWithTooltip :date-time="planPeriodEnd" underline />
              </div>
              <StripeManageButton
                v-if="hasPlan"
                v-model="stripeLoading"
                :multi-line="false"
                color="primary"
                :outlined="false"
                :message="'subscriptions.manageBilling'"
                :disabled="!stripeLoading && stripeDisabled"
              />
              <v-btn
                v-else
                color="primary"
                depressed
                class="text-none"
                href="#upgrade"
              >
                {{ $t('profile.upgradeTitle') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row v-if="statsLoading" class="mb-4">
        <v-col cols="12">
          <v-skeleton-loader type="card, article" />
        </v-col>
      </v-row>

      <v-alert v-else-if="statsError" type="error" text class="mb-6">
        {{ $t('profile.statsError') }}
      </v-alert>

      <template v-else>
        <v-row class="mb-8">
          <v-col v-for="card in statCards" :key="card.label" cols="12" sm="6" md="3">
            <v-card
              outlined
              class="stat-card fill-height"
              :class="{ 'stat-card--link': !!card.to }"
              v-bind="card.to ? { to: card.to } : {}"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-3">
                  <v-avatar size="36" :color="card.color" class="mr-3">
                    <v-icon small color="white">{{ card.icon }}</v-icon>
                  </v-avatar>
                  <div class="caption text--secondary text-uppercase font-weight-medium">
                    {{ card.label }}
                  </div>
                  <v-spacer />
                  <v-icon
                    v-if="card.to"
                    small
                    class="text--secondary"
                  >
                    chevron_right
                  </v-icon>
                </div>
                <div class="text-h4 font-weight-bold">{{ card.value }}</div>
                <div v-if="card.hint" class="caption text--secondary mt-1">{{ card.hint }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card v-if="planLoaded && !hasPlan" outlined class="mb-8">
          <v-card-text class="pa-6">
            <div class="body-2 mb-2">{{ freePlanUsageExplain }}</div>
            <v-progress-linear
              :value="freeAnalysesUsagePercent"
              :color="usageColor"
              height="8"
              rounded
            />
            <div class="caption text--secondary mt-2">
              {{ $t('subscriptions.usage.freeAnalysesReset') }}
            </div>
            <div v-if="extraInspirations > 0" class="caption mt-2">
              {{ $t('subscriptions.usage.extraInspirationsAvailable', [extraInspirations]) }}
            </div>
          </v-card-text>
        </v-card>

        <v-card v-if="planLoaded && hasPlan" outlined class="mb-8">
          <v-card-text class="pa-6">
            <div class="body-2 mb-2">{{ usageTooltip }}</div>
            <v-progress-linear
              v-if="hasLimitedPlan && maxInspirationsThisPeriod > 0"
              :value="usagePercent"
              :color="usageColor"
              height="8"
              rounded
            />
            <div v-if="extraInspirations > 0" class="caption mt-2">
              {{ $t('subscriptions.usage.extraInspirationsAvailable', [extraInspirations]) }}
            </div>
            <div v-if="overview && overview.sampled < overview.total" class="caption text--secondary mt-2">
              {{ $t('profile.statsSample', [overview.sampled]) }}
            </div>
          </v-card-text>
        </v-card>
      </template>

      <div id="upgrade">
        <SubscriptionSelection :read-only="hasPlan" embedded />
      </div>

      <v-card class="affiliate-card mt-10" outlined>
        <v-card-text class="pa-6 d-flex flex-column flex-md-row align-center">
          <v-avatar color="primary" size="48" class="mr-md-5 mb-4 mb-md-0">
            <v-icon color="white">volunteer_activism</v-icon>
          </v-avatar>
          <div class="flex-grow-1 text-center text-md-left mb-4 mb-md-0">
            <div class="text-h6 font-weight-medium">
              {{ $t('subscriptions.affiliateTitle') }}
            </div>
            <div class="text--secondary mt-1">
              {{ $t('affiliate') }}
            </div>
          </div>
          <v-btn
            color="primary"
            depressed
            class="text-none"
            :href="ExternalLinks.affiliateRegister"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('subscriptions.affiliateCta') }}
            <v-icon small class="ml-2">open_in_new</v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </UserLayout>
</template>

<script lang="ts">
    import Vue from 'vue';
    import UserLayout from "@/layouts/UserLayout.vue";
    import StripeManageButton from "@/components/account/StripeManageButton.vue";
    import SubscriptionSelection from "@/components/subscriptions/SubscriptionSelection.vue";
    import UserPlanMixin from "@/mixins/UserPlanMixin";
    import DateTimeWithTooltip from "@/components/DateTimeWithTooltip.vue";
    import {EventBus} from "@/utils/EventBus";
    import {ExternalLinks} from "@/config/ExternalLinks";
    import {Firestore} from "@/plugins/Firestore";
    import {captureException} from "@/plugins/Sentry";

    type ArticleOverview = Awaited<ReturnType<typeof Firestore.getUserArticleOverview>>;

    export default Vue.extend({
        name: "AccountView",
        components: {DateTimeWithTooltip, SubscriptionSelection, StripeManageButton, UserLayout},
        mixins: [UserPlanMixin],
        data: () => ({
            stripeLoading: false,
            stripeDisabled: false,
            ExternalLinks: ExternalLinks,
            statsLoading: true,
            statsError: false,
            overview: null as ArticleOverview | null,
        }),
        computed: {
            displayName(): string {
                const u = this.user as { name?: string; email?: string } | null | undefined;
                return (u && (u.name || u.email)) || '';
            },
            initials(): string {
                const source = String(this.displayName || '?').trim();
                const parts = source.split(/[\s@._-]+/).filter(Boolean);
                const letters = parts.slice(0, 2).map((p: string) => p.charAt(0).toUpperCase());
                return (letters.join('') || '?').slice(0, 2);
            },
            usagePercent(): number {
                if (!this.maxInspirationsThisPeriod) return 0;
                return Math.min(100, (this.articlesCreatedThisPeriod / this.maxInspirationsThisPeriod) * 100);
            },
            periodUsageLabel(): string {
                if (!this.planLoaded || !this.usageCounterLoaded) {
                    return String(this.$t('subscriptions.usageLoading'));
                }
                if (this.hasUnlimitedPlan) {
                    return String(this.articlesCreatedThisPeriod);
                }
                return `${this.articlesCreatedThisPeriod} / ${this.maxInspirationsThisPeriod || 0}`;
            },
            freeAnalysesUsagePercent(): number {
                if (!this.maxFreeAnalyses) return 0;
                return Math.min(100, (this.freeAnalysesUsed / this.maxFreeAnalyses) * 100);
            },
            freePlanUsageExplain(): string {
                if (this.freeAnalysesLeft <= 0) {
                    return String(this.$t('subscriptions.usage.freeAnalysesNone'));
                }
                return String(this.$t('subscriptions.usage.freeAnalysesExplain', {
                    max: this.maxFreeAnalyses,
                    used: Math.max(this.analyzeArticlesThisMonth, this.freeAnalysesUsed),
                    left: this.freeAnalysesLeft,
                }));
            },
            statCards(): Array<{ label: string; value: string; hint?: string; icon: string; color: string; to?: string }> {
                const o = this.overview;
                const avg = o?.avgSeoScore;
                const best = o?.bestSeoScore;
                return [
                    {
                        label: String(this.$t('profile.articlesTotal')),
                        value: o ? String(o.total) : '—',
                        icon: 'library_books',
                        color: 'primary',
                        to: '/',
                    },
                    {
                        label: String(this.$t('profile.articlesThisPeriod')),
                        value: this.periodUsageLabel,
                        hint: this.hasPlan ? undefined : String(this.$t('subscriptions.noPlan')),
                        icon: 'auto_stories',
                        color: 'info',
                    },
                    {
                        label: String(this.$t('profile.avgSeo')),
                        value: avg == null ? String(this.$t('profile.noScore')) : `${avg} %`,
                        icon: 'insights',
                        color: 'success',
                    },
                    {
                        label: String(this.$t('profile.bestSeo')),
                        value: best == null ? String(this.$t('profile.noScore')) : `${best} %`,
                        icon: 'emoji_events',
                        color: 'warning',
                    },
                    {
                        label: String(this.$t('profile.generated')),
                        value: o ? String(o.generated) : '—',
                        icon: 'edit_note',
                        color: 'ai',
                    },
                    {
                        label: String(this.$t('profile.analyzed')),
                        value: o ? String(o.analyzed) : '—',
                        icon: 'travel_explore',
                        color: 'primary',
                    },
                    {
                        label: String(this.$t('profile.freeAnalyses')),
                        value: this.planLoaded
                            ? `${this.freeAnalysesLeft} / ${this.maxFreeAnalyses}`
                            : String(this.$t('subscriptions.usageLoading')),
                        hint: this.planLoaded
                            ? String(this.$t('profile.freeAnalysesHint', [this.freeAnalysesLeft, this.maxFreeAnalyses]))
                            : String(this.$t('subscriptions.usageLoadingTooltip')),
                        icon: 'analytics',
                        color: 'info',
                    },
                    {
                        label: String(this.$t('profile.extraCredits')),
                        value: String(this.extraInspirations),
                        icon: 'add_circle',
                        color: 'accent',
                    },
                ];
            },
        },
        watch: {
            stripeLoading() {
                EventBus.$emit("stripeLoading");
            },
            '$store.getters.authenticated': {
                immediate: false,
                handler(authenticated: boolean) {
                    if (authenticated && !this.overview && !this.statsLoading) {
                        this.loadOverview();
                    }
                }
            }
        },
        created(): void {
            EventBus.$on("stripeLoading", () => {
                this.stripeDisabled = true;
            });
            if (this.$store.getters.authenticated) {
                this.loadOverview();
            } else {
                this.statsLoading = false;
            }
        },
        methods: {
            loadOverview(): void {
                this.statsLoading = true;
                this.statsError = false;
                Firestore.getUserArticleOverview()
                    .then(overview => {
                        this.overview = overview;
                    })
                    .catch(error => {
                        this.statsError = true;
                        captureException(error);
                    })
                    .finally(() => {
                        this.statsLoading = false;
                    });
            },
        }
    });
</script>

<style lang="sass" scoped>
.account-page
    max-width: 1280px

.profile-hero,
.stat-card,
.affiliate-card
    border-radius: 16px !important

.stat-card--link
    cursor: pointer
    transition: box-shadow 0.15s ease, border-color 0.15s ease

    &:hover
        border-color: var(--v-primary-base) !important
        box-shadow: 0 6px 20px rgba(8, 135, 220, 0.12)
</style>
